/**
 * Type definitions for window message communication between BlockNote components
 */

/**
 * Data structure for drag and drop operations containing block information
 */
export type DragDropBlockData = {
  id: string
  text: string
}

/**
 * Message data sent when a week reference block is dropped
 */
export type DroppedWeekReferenceBlockMessage = {
  type: 'droppedWeekReferenceBlock'
  id: string
}

/**
 * Union type for all possible window message data types
 */
export type WindowMessageData = DroppedWeekReferenceBlockMessage

/**
 * Type guard to check if data has the structure of DragDropBlockData
 */
export function isDragDropBlockData(data: unknown): data is DragDropBlockData {
  return (
    typeof data === 'object' &&
    data != null &&
    'id' in data &&
    typeof data.id === 'string' &&
    'text' in data &&
    typeof data.text === 'string'
  )
}

/**
 * Type guard to check if a message data object is a DroppedWeekReferenceBlockMessage
 */
export function isDroppedWeekReferenceBlockMessage(
  data: unknown
): data is DroppedWeekReferenceBlockMessage {
  return (
    typeof data === 'object' &&
    data != null &&
    'type' in data &&
    data.type === 'droppedWeekReferenceBlock' &&
    'id' in data &&
    typeof data.id === 'string'
  )
}

/**
 * Type guard to check if a message data object is valid WindowMessageData
 */
export function isValidWindowMessageData(
  data: unknown
): data is WindowMessageData {
  return isDroppedWeekReferenceBlockMessage(data)
}

/**
 * Typed MessageEvent for BlockNote window messages
 */
export type TypedMessageEvent<T = WindowMessageData> = {
  data: T
} & Omit<MessageEvent, 'data'>
